import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { normalizeSiteSlug, setSiteSlug as persistSiteSlug } from "../services/api";
import { resolveSiteBundle } from "../services/siteService";

const AppContext = createContext(null);

const DEFAULT_SITE_SETTINGS = {
  siteName: "Solusi IoT Cerdas",
  siteTagline: " Website Toko Online Solusi IoT Cerdas, CMS, Marketplace, dan IoT Modern",
  siteDescription: "Platform Solusi IoT Cerdas untuk website company profile, marketplace, dan dashboard admin CMS.",
  contactEmail: "budi@solusipemetaanpasti.my.id",
  phoneNumber: "",
  whatsappNumber: "",
  address: "Indonesia",
  currency: "IDR",
  locale: "id-ID",
  theme: "light",
  heroTitle: "",
  heroSubtitle: "",
  logo: "",
  favicon: "",
  primaryColor: "",
  secondaryColor: "",
  socialLinks: {
    instagram: "",
    tiktok: "",
    youtube: "",
    facebook: "",
    linkedin: "",
  },
};

const STORAGE_KEYS = {
  theme: "sic_theme",
  siteSettings: "sic_site_settings",
};

function getSettingValue(settingsMap = {}, ...keys) {
  for (const key of keys) {
    const value = settingsMap?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
}

function normalizeSiteSettings(raw) {
  if (!raw || typeof raw !== "object") return DEFAULT_SITE_SETTINGS;

  const site = raw?.site || {};
  const settingsMap = raw?.settings_map || raw?.settingsMap || raw?.settings || {};

  return {
    siteName: site?.name || raw?.siteName || getSettingValue(settingsMap, "site_name", "siteName") || DEFAULT_SITE_SETTINGS.siteName,
    siteTagline: raw?.siteTagline || getSettingValue(settingsMap, "tagline", "site_tagline", "siteTagline") || DEFAULT_SITE_SETTINGS.siteTagline,
    siteDescription: raw?.siteDescription || getSettingValue(settingsMap, "site_description", "siteDescription", "description") || DEFAULT_SITE_SETTINGS.siteDescription,
    contactEmail: site?.email || raw?.contactEmail || getSettingValue(settingsMap, "email", "contact_email", "contactEmail") || DEFAULT_SITE_SETTINGS.contactEmail,
    phoneNumber: raw?.phoneNumber || getSettingValue(settingsMap, "phone_number", "phone", "phoneNumber") || DEFAULT_SITE_SETTINGS.phoneNumber,
    whatsappNumber: site?.whatsapp_number || raw?.whatsappNumber || getSettingValue(settingsMap, "whatsapp_number", "whatsappNumber", "whatsapp") || DEFAULT_SITE_SETTINGS.whatsappNumber,
    address: site?.address || raw?.address || getSettingValue(settingsMap, "address") || DEFAULT_SITE_SETTINGS.address,
    currency: raw?.currency || getSettingValue(settingsMap, "currency") || DEFAULT_SITE_SETTINGS.currency,
    locale: raw?.locale || getSettingValue(settingsMap, "locale") || DEFAULT_SITE_SETTINGS.locale,
    theme: raw?.theme || getSettingValue(settingsMap, "theme") || DEFAULT_SITE_SETTINGS.theme,
    heroTitle: raw?.heroTitle || getSettingValue(settingsMap, "hero_title", "heroTitle") || DEFAULT_SITE_SETTINGS.heroTitle,
    heroSubtitle: raw?.heroSubtitle || getSettingValue(settingsMap, "hero_subtitle", "heroSubtitle") || DEFAULT_SITE_SETTINGS.heroSubtitle,
    logo: site?.logo || raw?.logo || getSettingValue(settingsMap, "logo") || DEFAULT_SITE_SETTINGS.logo,
    favicon: site?.favicon || raw?.favicon || getSettingValue(settingsMap, "favicon") || DEFAULT_SITE_SETTINGS.favicon,
    primaryColor: site?.primary_color || raw?.primaryColor || getSettingValue(settingsMap, "primary_color", "primaryColor") || DEFAULT_SITE_SETTINGS.primaryColor,
    secondaryColor: site?.secondary_color || raw?.secondaryColor || getSettingValue(settingsMap, "secondary_color", "secondaryColor") || DEFAULT_SITE_SETTINGS.secondaryColor,
    socialLinks: {
      instagram: getSettingValue(settingsMap, "instagram_url", "instagram") || DEFAULT_SITE_SETTINGS.socialLinks.instagram,
      tiktok: getSettingValue(settingsMap, "tiktok_url", "tiktok") || DEFAULT_SITE_SETTINGS.socialLinks.tiktok,
      youtube: getSettingValue(settingsMap, "youtube_url", "youtube") || DEFAULT_SITE_SETTINGS.socialLinks.youtube,
      facebook: getSettingValue(settingsMap, "facebook_url", "facebook") || DEFAULT_SITE_SETTINGS.socialLinks.facebook,
      linkedin: getSettingValue(settingsMap, "linkedin_url", "linkedin") || DEFAULT_SITE_SETTINGS.socialLinks.linkedin,
    },
  };
}

export function AppProvider({ children }) {
  const [siteSettings, setSiteSettings] = useState(DEFAULT_SITE_SETTINGS);
  const [theme, setTheme] = useState("light");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [appReady, setAppReady] = useState(false);
  const [currentSite, setCurrentSite] = useState(null);
  const [settingsMap, setSettingsMap] = useState({});

  useEffect(() => {
    const bootApp = async () => {
      try {
        const savedTheme = localStorage.getItem(STORAGE_KEYS.theme);
        const savedSettings = localStorage.getItem(STORAGE_KEYS.siteSettings);

        if (savedTheme === "dark" || savedTheme === "light") {
          setTheme(savedTheme);
        }

        if (savedSettings) {
          const parsed = JSON.parse(savedSettings);
          const normalized = normalizeSiteSettings(parsed);
          setSiteSettings(normalized);
          setTheme(normalized.theme || savedTheme || "light");
        }

        const remote = await resolveSiteBundle();
        const normalizedRemote = normalizeSiteSettings(remote);
        setSiteSettings(normalizedRemote);
        setTheme((prev) => normalizedRemote.theme || prev);
        setCurrentSite(remote?.site || null);
        setSettingsMap(remote?.settings_map || {});

        if (remote?.site?.slug) {
          persistSiteSlug(remote.site.slug);
        }
      } catch (error) {
        console.warn("Gagal mengambil public site settings:", error);
      } finally {
        setAppReady(true);
      }
    };

    bootApp();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.theme, theme);
    } catch (error) {
      console.error("Gagal menyimpan theme:", error);
    }

    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEYS.siteSettings,
        JSON.stringify({ site: currentSite, settings_map: settingsMap, ...siteSettings })
      );
    } catch (error) {
      console.error("Gagal menyimpan site settings:", error);
    }
  }, [siteSettings, currentSite, settingsMap]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const updateSiteSettings = useCallback((nextSettings) => {
    setSiteSettings((prev) => ({
      ...prev,
      ...nextSettings,
      socialLinks: {
        ...prev.socialLinks,
        ...(nextSettings?.socialLinks || {}),
      },
    }));

    if (nextSettings?.theme) {
      setTheme(nextSettings.theme);
    }

    if (nextSettings?.siteSlug) {
      persistSiteSlug(normalizeSiteSlug(nextSettings.siteSlug));
    }
  }, []);

  const value = useMemo(
    () => ({
      appReady,
      siteSettings,
      setSiteSettings,
      updateSiteSettings,
      theme,
      setTheme,
      toggleTheme,
      sidebarOpen,
      setSidebarOpen,
      currentSite,
      siteId: currentSite?.id || null,
      siteSlug: normalizeSiteSlug(currentSite?.slug),
      settingsMap,
    }),
    [appReady, siteSettings, updateSiteSettings, theme, toggleTheme, sidebarOpen, currentSite, settingsMap]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp harus digunakan di dalam AppProvider");
  }
  return context;
}
